import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { useRequest } from 'vue-request';
import * as apiList from '@/server/api';
import _ from 'lodash';
const useNormalFetch = (service, options = {
  onSuccess: undefined,
  // (res) => {}
  onError: undefined // (err) => {}
}) => {
  const loading = ref(false);
  const error = ref('');
  const data = ref();
  const params = ref([]);
  const {
    onSuccess,
    onError
  } = options || {};
  const doSuccess = res => {
    !!onSuccess && onSuccess(res);
  };
  const doError = err => {
    !!onError && onError(err);
  };
  const runAsync = (...args) => {
    params.value = [...args];
    loading.value = true;
    error.value = '';
    return new Promise((resolve, reject) => {
      service(...args).then(res => {
        error.value = '';
        data.value = res;
        loading.value = false;
        doSuccess(res);
        resolve(res);
      }).catch(err => {
        error.value = err;
        loading.value = false;
        doError(err);
        reject(err);
      });
    });
  };
  const run = (...args) => {
    loading.value = true;
    error.value = '';
    params.value = [...args];
    service(...args).then(res => {
      data.value = res;
      loading.value = false;
      doSuccess(res);
    }).catch(err => {
      error.value = err;
      loading.value = false;
      doError(err);
    });
  };
  const margeQueryParams = (...args) => {
    const newParams = [...params.value];
    for (let i = 0; i < args.length; i++) {
      if (typeof args[i] === 'object') {
        Object.assign(newParams[i], args[i]);
      } else {
        newParams[i] = args[i];
      }
    }
    return newParams;
  };
  const refresh = (...args) => {
    const queryParams = margeQueryParams(...args);
    params.value = [...queryParams];
    loading.value = true;
    error.value = '';
    service(...queryParams).then(res => {
      loading.value = false;
      data.value = res;
      doSuccess(res);
    }).catch(err => {
      error.value = err;
      loading.value = false;
      doError(err);
    });
  };
  const refreshAsync = async (...args) => {
    const queryParams = margeQueryParams(...args);
    params.value = [...queryParams];
    loading.value = true;
    error.value = '';
    return new Promise((resolve, reject) => {
      service(...queryParams).then(res => {
        loading.value = false;
        data.value = res;
        doSuccess(res);
        resolve(res);
      }).catch(err => {
        error.value = err;
        loading.value = false;
        doError(err);
        reject(err);
      });
    });
  };
  return {
    loading,
    data,
    error,
    run,
    runAsync,
    refresh,
    refreshAsync
  };
};

// 防抖查询
export const useDebounceRequest = (service, options = {}) => {
  return useRequest(service, {
    debounceInterval: 300,
    manual: true,
    loadingKeep: 300,
    ...options
  });
};

/**
 * 根据接口名，含返回接口名函数getName的object或函数，返回带接口状态分发的高级函数
 * @param fnOrName
 * @param options {undefined|{ showError: boolean }}
 * @returns {function(params:Record<string,any>, options?:{ showError: boolean}):Promise<any>}
 * @constructor
 */
export const getAPI = (fnOrName, options = undefined) => {
  const notFoundAPIProxy = async (params, options) => {
    return Promise.resolve({
      error: `API[${fnOrName}] not found`
    });
  };
  const api = fn => {
    if (!options || typeof options !== 'object') {
      return fn;
    }
    return async params => {
      return fn(params, {
        showError: options.showError || false
      });
    };
  };
  if (typeof fnOrName === 'string') {
    const fn = _.get(apiList, fnOrName);
    if (!fn) {
      return notFoundAPIProxy;
    }
    return api(fn);
  }
  if (typeof fnOrName === 'object' && typeof fnOrName.getName === 'function') {
    const name = fnOrName.getName();
    const fn = _.get(apiList, name);
    if (!fn) {
      return notFoundAPIProxy;
    }
    return api(fn);
  }
  if (typeof fnOrName === 'function') {
    return api(fnOrName);
  }
  return notFoundAPIProxy;
};

/**
 * 调用api
 * @param fnOrName {string|Function|{ getName():string }}
 * @param params {Record<string,any>}
 * @param options {{ showError:boolean}}
 * @returns {Promise<Omit<{error: string}|Record<string,any>>>}
 */
export const callAPI = async (fnOrName, params = {}, options = {
  showError: true
}) => {
  try {
    options = options || {
      showError: true
    };
    const fn = getAPI(fnOrName);
    if (fn) {
      return fn(params, options || {
        showError: true
      });
    }
    return Promise.resolve({
      error: `API[${fnOrName}] not found`
    });
  } catch (e) {
    return Promise.resolve({
      error: e.message
    });
  }
};

// 防抖查询数据接口
export const fetchApi = (fnOrName, {
  debounce = {
    disabled: false,
    loadingKeep: 200
  },
  showError = true,
  onSuccess = () => {},
  onError = () => {}
} = {}) => {
  const debounceEnabled = !!debounce && !debounce.disabled;
  // fn需要在调用时再获取，通常在setup中使用fetchApi绑定api,如果在此处就调用查找api，如果fnOrName为含有getName方法的对象时，如果是响应值时，可能得不到正确的api名称
  // 所以service应该为被调用时再查找api
  const service = params => {
    return callAPI(fnOrName, params, {
      showError
    });
  };
  const callbacks = {
    onSuccess: res => {
      !!onSuccess && onSuccess(res);
    },
    onError: err => {
      !!onError && onError(err);
    }
  };
  const query = debounceEnabled ? useDebounceRequest(service, {
    ...debounce,
    ...callbacks
  }) : useNormalFetch(service, {
    ...callbacks
  });
  const {
    data,
    run,
    runAsync,
    refresh,
    refreshAsync,
    loading,
    error
  } = query;
  return {
    data,
    run,
    refresh,
    refreshAsync: async (...args) => {
      try {
        return await refreshAsync(...args);
      } catch (err) {
        return Promise.resolve({
          error: err.message
        });
      }
    },
    runAsync: async (...args) => {
      try {
        return await runAsync(...args);
      } catch (err) {
        return Promise.resolve({
          error: err.message
        });
      }
    },
    error,
    loading
  };
};

// 防抖查询数据接口
export const fetchAllApi = (fnOrNameArr = [], {
  debounce = {
    disabled: false,
    loadingKeep: 200
  },
  showError = true,
  onSuccess = () => {},
  onError = () => {}
} = {}) => {
  const debounceEnabled = !!debounce && !debounce.disabled;
  const apis = (fnOrNameArr || []).map(fnOrName => {
    return params => {
      return callAPI(fnOrName, params, {
        showError
      });
    };
  });
  const callbacks = {
    onSuccess: res => {
      !!onSuccess && onSuccess(res);
    },
    onError: err => {
      !!onError && onError(err);
    }
  };
  const service = (...args) => {
    return new Promise((resolve, reject) => {
      const tasks = [];
      for (let i = 0; i < apis.length; i++) {
        const curTask = () => new Promise((resolve, reject) => {
          apis[i](...(args.length > i ? args[i] : [])).then(resolve).catch(reject);
        });
        tasks.push(curTask());
      }
      Promise.all(tasks).then(resolve).catch(reject);
    });
  };
  const request = debounceEnabled ? useDebounceRequest(service, {
    ...debounce,
    ...callbacks
  }) : useNormalFetch(service, {
    ...callbacks
  });
  const {
    data,
    run,
    runAsync,
    refresh,
    refreshAsync,
    loading,
    error
  } = request;
  return {
    data,
    run,
    refresh,
    refreshAsync: async (...args) => {
      try {
        return await refreshAsync(...args);
      } catch (err) {
        return Promise.resolve({
          error: err.message
        });
      }
    },
    runAsync: async (...args) => {
      try {
        return await runAsync(...args);
      } catch (err) {
        return Promise.resolve({
          error: err.message
        });
      }
    },
    error,
    loading
  };
};